import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Stepper as MuiStepper,
  Step as MuiStep,
  StepLabel,
  StepContent,
  StepConnector,
  StepIcon as MuiStepIcon,
  CircularProgress,
  styled,
  Theme,
} from '@mui/material';
import { Button } from '@/components/atoms';
import {
  Check as CheckIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon,
  Schedule as PendingIcon,
  SkipNext as SkipIcon,
  RadioButtonUnchecked as PendingNumberIcon,
  PlayArrow as ActiveIcon,
  Block as DisabledIcon,
} from '@mui/icons-material';
import {
  StepperProps,
  Step,
  StepState,
  StepIconConfig,
  StepStyleConfig,
  StepStatus,
} from './Stepper.props';
import { stepperTheme } from './Stepper.theme';

// Helper Functions
/**
 * Get colors from theme palette
 */
const getThemeColors = (theme: Theme) => ({
  primary:
    (theme.palette as any)?.M3?.sys?.light?.primary ||
    (theme.palette.primary as any)?.main ||
    '#5069EC',
  surface:
    (theme.palette as any)?.M3?.sys?.light?.surface ||
    theme.palette.background?.paper ||
    '#FFFFFF',
  pendingBackground:
    (theme.palette as any)?.M3?.ref?.neutral?.neutral70 ||
    theme.palette.grey?.[400] ||
    '#ADAAAA',
  pendingText: theme.palette.common?.white || '#FFFFFF',
  error: (theme.palette.error as any)?.main || '#B00020',
  warning: (theme.palette.warning as any)?.main || '#F57C00',
  onSurfaceVariant:
    (theme.palette as any)?.M3?.sys?.light?.['on-surface-variant'] ||
    theme.palette.text?.secondary ||
    '#4E5C79',
  success: (theme.palette.success as any)?.main || '#008933',
  disabled: theme.palette.grey?.[100] || '#F5F5F5',
  disabledText: theme.palette.grey?.[400] || '#BDBDBD',
  neutralVariant50:
    (theme.palette as any)?.M3?.ref?.neutral?.['neutral-variant-50'] ||
    theme.palette.grey?.[600] ||
    '#757575',
  surfaceContainer:
    (theme.palette as any)?.M3?.sys?.light?.['surface-container'] ||
    theme.palette.background?.default ||
    '#FFFFFF',
  connectorDefault:
    (theme.palette as any)?.M3?.sys?.light?.['on-surface-variant'] ||
    theme.palette.grey?.[300] ||
    '#DCE3EB',
  timelinePending:
    (theme.palette as any)?.M3?.sys?.light?.['surface-variant'] ||
    theme.palette.grey?.[200] ||
    '#EEF2F5',
  timelinePendingBorder:
    (theme.palette as any)?.M3?.ref?.neutral?.['neutral-variant-60'] ||
    theme.palette.grey?.[400] ||
    '#BCC6D2',
  black: theme.palette.common?.black || '#000000',
});

/**
 * Check if step status matches a given state
 */
const isStepStatus = (
  status: string | undefined,
  targetStatus: StepStatus
): boolean => {
  return status === targetStatus;
};

/**
 * Get background color based on step status for timeline mode
 */
const getTimelineStatusBackground = (
  status: string | undefined,
  themeColors: ReturnType<typeof getThemeColors>
): string => {
  if (isStepStatus(status, StepStatus.COMPLETED)) {
    return themeColors.success;
  }
  if (isStepStatus(status, StepStatus.IN_PROGRESS)) {
    return 'transparent';
  }
  return themeColors.timelinePending;
};

/**
 * Get border style based on step status for timeline mode
 */
const getTimelineStatusBorder = (
  status: string | undefined,
  themeColors: ReturnType<typeof getThemeColors>,
  theme: Theme
): string => {
  if (
    isStepStatus(status, StepStatus.COMPLETED) ||
    isStepStatus(status, StepStatus.IN_PROGRESS)
  ) {
    return 'none';
  }
  return `${theme.unit(0.5)} dashed ${themeColors.timelinePendingBorder}`; // 2px (0.5 × 4 = 2)
};

/**
 * Get substep dot color based on status for timeline mode
 */
const getSubstepDotColor = (
  status: string | undefined,
  themeColors: ReturnType<typeof getThemeColors>
): string => {
  if (isStepStatus(status, StepStatus.COMPLETED)) {
    return themeColors.success;
  }
  if (isStepStatus(status, StepStatus.IN_PROGRESS)) {
    return themeColors.primary;
  }
  return themeColors.timelinePendingBorder;
};

/**
 * Get border string with theme units
 */
const getBorderString = (theme: Theme, borderColor?: string): string => {
  return borderColor ? `${theme.unit(0.5)} solid ${borderColor}` : 'none'; // 2px (0.5 × 4 = 2)
};

// Default icons for each state
const DEFAULT_ICONS: Required<StepIconConfig> = {
  active: <ActiveIcon />,
  completed: <CheckIcon />,
  error: <ErrorIcon />,
  warning: <WarningIcon />,
  pending: <PendingNumberIcon />,
  skipped: <SkipIcon />,
  disabled: <DisabledIcon />,
  showNumbers: true,
};

// Custom Step Icon Component with configurable styles
interface CustomStepIconRootProps {
  ownerState: {
    completed: boolean;
    active: boolean;
    state: string;
    size: string;
    customStyles?: StepStyleConfig;
    enableTransitions?: boolean;
    enableHover?: boolean;
    duration?: number;
  };
}

const CustomStepIconRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'ownerState',
  // @ts-expect-error - MUI styled component type issue with ownerState prop
})<CustomStepIconRootProps>(({ theme, ownerState }) => {
  const {
    completed,
    active,
    state,
    size,
    customStyles,
    enableTransitions = true,
    enableHover = true,
    duration = 200,
  } = ownerState;

  const getSizeStyles = () => {
    // Use theme size configuration if available
    const themeSize = (theme as any).size?.stepper;
    const sizeConfig = themeSize ? themeSize[size] : null;
    if (sizeConfig) {
      return {
        width: sizeConfig.width,
        height: sizeConfig.height,
        fontSize: sizeConfig.fontSize,
        borderRadius: sizeConfig.borderRadius || '50%',
      };
    }
    // Fallback for standard sizes
    switch (size) {
      case 's':
        return {
          width: 24,
          height: 24,
          fontSize: theme.typography.M3.label.small.fontSize,
          borderRadius: '12px',
        };
      case 'l':
        return {
          width: 40,
          height: 40,
          fontSize: theme.typography.M3.label.large.fontSize,
          borderRadius: '20px',
        };
      case 'xl':
        return {
          width: 48,
          height: 48,
          fontSize: theme.typography.M3.label.large.fontSize,
          borderRadius: '24px',
        };
      default:
        return {
          width: 24,
          height: 24,
          fontSize: theme.typography.M3.label.medium.fontSize,
          borderRadius: '12px',
        };
    }
  };

  const getStateStyles = () => {
    const themeColors = getThemeColors(theme);

    // Get custom styles for the current state
    const getCustomStyleForState = (stateName: string) =>
      customStyles?.[stateName as keyof StepStyleConfig];

    let stateStyles = {};

    if (completed) {
      const completedCustom = getCustomStyleForState(StepStatus.COMPLETED);
      stateStyles = {
        backgroundColor:
          completedCustom?.backgroundColor || themeColors.primary,
        color: completedCustom?.color || themeColors.surface,
        borderRadius: '20px',
        border: getBorderString(theme, completedCustom?.borderColor),
      };
    } else if (active) {
      const activeCustom = getCustomStyleForState(StepStatus.ACTIVE);
      stateStyles = {
        backgroundColor: activeCustom?.backgroundColor || themeColors.primary,
        color: activeCustom?.color || themeColors.surface,
        borderRadius: '20px',
        border: getBorderString(theme, activeCustom?.borderColor),
      };
    } else if (state === StepStatus.ERROR) {
      const errorCustom = getCustomStyleForState(StepStatus.ERROR);
      stateStyles = {
        backgroundColor: errorCustom?.backgroundColor || themeColors.error,
        color: errorCustom?.color || themeColors.surface,
        border: getBorderString(theme, errorCustom?.borderColor),
      };
    } else if (state === StepStatus.WARNING) {
      const warningCustom = getCustomStyleForState(StepStatus.WARNING);
      stateStyles = {
        backgroundColor: warningCustom?.backgroundColor || themeColors.warning,
        color: warningCustom?.color || themeColors.surface,
        border: getBorderString(theme, warningCustom?.borderColor),
      };
    } else if (state === StepStatus.SKIPPED) {
      const skippedCustom = getCustomStyleForState(StepStatus.SKIPPED);
      stateStyles = {
        backgroundColor:
          skippedCustom?.backgroundColor || themeColors.pendingBackground,
        color: skippedCustom?.color || themeColors.pendingText,
        border: getBorderString(theme, skippedCustom?.borderColor),
      };
    } else if (state === StepStatus.DISABLED) {
      const disabledCustom = getCustomStyleForState(StepStatus.DISABLED);
      stateStyles = {
        backgroundColor:
          disabledCustom?.backgroundColor || themeColors.disabled,
        color: disabledCustom?.color || themeColors.disabledText,
        border: getBorderString(theme, disabledCustom?.borderColor),
        opacity: 0.5,
      };
    } else {
      // Pending state
      const pendingCustom = getCustomStyleForState(StepStatus.PENDING);
      stateStyles = {
        backgroundColor:
          pendingCustom?.backgroundColor || themeColors.pendingBackground,
        color: pendingCustom?.color || themeColors.pendingText,
        borderRadius: '20px',
        border: getBorderString(theme, pendingCustom?.borderColor),
      };
    }

    return stateStyles;
  };

  const baseStyles = {
    // .MasterStepperCircle - Badge (24 × 24)
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    padding: 0, // No padding - badge is exactly 24x24
    width: 24,
    height: 24,
    borderRadius: '50%', // Make it circular
    // Inside auto layout
    flex: 'none',
    order: 0,
    flexGrow: 0,
    // M3/label/medium for text inside circle
    ...theme.typography.M3.label.medium,
    textAlign: 'center',
    zIndex: 1,
    ...getStateStyles(),
  };

  const animationStyles = enableTransitions
    ? {
        transition: `all ${duration}ms ease-in-out`,
        ...(enableHover && {
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: `0 2px 8px ${alpha(theme.palette.common?.black || theme.palette.grey?.[900] || '#000', 0.15)}`,
          },
        }),
      }
    : {};

  return {
    ...baseStyles,
    ...animationStyles,
  } as const;
});

const NumberedStepIcon = (props: any) => {
  const {
    active,
    completed,
    icon,
    state,
    size = 'm',
    theme,
    iconConfig = DEFAULT_ICONS,
    styleConfig,
    enableTransitions,
    enableHover,
    duration,
  } = props;
  const stepNumber = icon;

  const ownerState = {
    completed,
    active,
    state,
    size,
    customStyles: styleConfig,
    enableTransitions,
    enableHover,
    duration,
  };

  // Get icon size based on size configuration and custom styles
  const getIconSize = () => {
    const customSize = styleConfig?.[state as keyof StepStyleConfig]?.iconSize;
    if (customSize) return customSize;

    switch (size) {
      case 's':
        return 16;
      case 'l':
        return 24;
      case 'xl':
        return 28;
      default:
        return 20;
    }
  };

  // Get the appropriate icon for the current state
  const getStateIcon = () => {
    const iconSize = getIconSize();
    const iconProps = { sx: { fontSize: iconSize } };

    if (completed) {
      return iconConfig.completed ? (
        React.cloneElement(
          iconConfig.completed as React.ReactElement,
          iconProps
        )
      ) : (
        <CheckIcon {...iconProps} />
      );
    }

    if (active) {
      // For active state, show custom icon or number based on configuration
      if (iconConfig.active && !iconConfig.showNumbers) {
        return React.cloneElement(
          iconConfig.active as React.ReactElement,
          iconProps
        );
      }
      return iconConfig.showNumbers
        ? stepNumber
        : iconConfig.active
          ? React.cloneElement(
              iconConfig.active as React.ReactElement,
              iconProps
            )
          : stepNumber;
    }

    switch (state) {
      case 'error':
        return iconConfig.error ? (
          React.cloneElement(iconConfig.error as React.ReactElement, iconProps)
        ) : (
          <ErrorIcon {...iconProps} />
        );
      case 'warning':
        return iconConfig.warning ? (
          React.cloneElement(
            iconConfig.warning as React.ReactElement,
            iconProps
          )
        ) : (
          <WarningIcon {...iconProps} />
        );
      case 'skipped':
        return iconConfig.skipped ? (
          React.cloneElement(
            iconConfig.skipped as React.ReactElement,
            iconProps
          )
        ) : (
          <SkipIcon {...iconProps} />
        );
      case 'disabled':
        return iconConfig.disabled ? (
          React.cloneElement(
            iconConfig.disabled as React.ReactElement,
            iconProps
          )
        ) : (
          <DisabledIcon {...iconProps} />
        );
      case 'pending':
      default:
        // For pending state, show custom icon or number based on configuration
        if (iconConfig.pending && !iconConfig.showNumbers) {
          return React.cloneElement(
            iconConfig.pending as React.ReactElement,
            iconProps
          );
        }
        return iconConfig.showNumbers
          ? stepNumber
          : iconConfig.pending
            ? React.cloneElement(
                iconConfig.pending as React.ReactElement,
                iconProps
              )
            : stepNumber;
    }
  };

  return (
    // @ts-expect-error - MUI styled component type issue with ownerState prop
    <CustomStepIconRoot ownerState={ownerState} as="div">
      {getStateIcon()}
    </CustomStepIconRoot>
  );
};

// Custom StepConnector with configurable styles
const CustomStepConnector = styled(StepConnector, {
  shouldForwardProp: (prop) =>
    !['connectorType', 'connectorStyle', 'iconSize'].includes(prop as string),
})<{ connectorType?: string; connectorStyle?: any; iconSize?: number }>(({
  theme,
  connectorType,
  connectorStyle,
  iconSize = 32,
}) => {
  const getConnectorStyle = () => {
    // M3/sys/light/on-surface-variant
    const themeColors = getThemeColors(theme);
    const defaultColor = connectorStyle?.color || themeColors.onSurfaceVariant;
    const height = connectorStyle?.height || 1; // Height: 1px (line thickness)
    const width = connectorStyle?.width || 104; // Width: 104px (fill)

    switch (connectorType) {
      case 'dashed':
        return {
          '& .MuiStepConnector-line': {
            borderColor: defaultColor,
            borderTopStyle: 'dashed',
            borderTopWidth: height,
            width: width,
            opacity: 1,
          },
        };
      case 'dotted':
        return {
          '& .MuiStepConnector-line': {
            borderColor: defaultColor,
            borderTopStyle: 'dotted',
            borderTopWidth: height,
            width: width,
            opacity: 1,
          },
        };
      case 'none':
        return {
          '& .MuiStepConnector-line': {
            display: 'none',
          },
        };
      default: // 'line'
        return {
          '& .MuiStepConnector-line': {
            // Rectangle 17 - line element
            borderColor: defaultColor, // M3/sys/light/on-surface-variant: #4E5C79
            borderTopStyle: 'solid', // Added: need solid style for border to show
            borderTopWidth: height, // 1px
            width: width, // 104px
            opacity: 1,
            // Inside auto layout
            flex: 'none',
            order: 0,
            alignSelf: 'stretch',
            flexGrow: 0,
          },
        };
    }
  };

  return {
    // .MasterStepperLine - Connector styling (104px with 6px gaps handled by positioning)
    display: 'block !important' as any,
    visibility: 'visible !important' as any,
    padding: 0,
    margin: 0, // Remove margin, use positioning instead
    minWidth: 104,
    width: 104,
    maxWidth: 104,
    height: 1,
    flex: 'none',
    flexGrow: 0,
    flexShrink: 0,
    ...getConnectorStyle(),
    '&.MuiStepConnector-alternativeLabel': {
      top: 12, // Center of 24px badge (24/2 = 12)
      // Each step is 140px wide
      // From step center: badge half (12px) + gap (6px) = 18px to start connector
      left: 'calc(-50% + 18px)', // Start 18px from center (after badge half + gap)
      right: 'auto',
      width: '104px !important' as any, // Connector is exactly 104px
      position: 'absolute',
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiStepConnector-line': {
      display: 'block !important' as any,
      visibility: 'visible !important' as any,
      minHeight: 1,
      height: 1,
      borderTopWidth: '1px !important' as any,
      borderTopStyle: 'solid !important' as any,
      borderColor: getThemeColors(theme).onSurfaceVariant,
      width: 104,
      minWidth: 104,
      maxWidth: 104,
      backgroundColor: 'transparent',
    },
    '&.MuiStepConnector-active': {
      '& .MuiStepConnector-line': {
        borderColor:
          connectorStyle?.activeColor || getThemeColors(theme).neutralVariant50,
      },
    },
    '&.MuiStepConnector-completed': {
      '& .MuiStepConnector-line': {
        borderColor:
          connectorStyle?.completedColor ||
          getThemeColors(theme).neutralVariant50,
      },
    },
    '&.MuiStepConnector-error': {
      '& .MuiStepConnector-line': {
        borderColor: connectorStyle?.errorColor || getThemeColors(theme).error,
      },
    },
    // Apply custom styles if provided
    ...(connectorStyle?.sx || {}),
  };
});

// Custom StepIcon component for styling
const StyledStepIcon = styled(MuiStepIcon, {
  shouldForwardProp: (prop) =>
    !['stepState', 'customColor', 'size'].includes(prop as string),
})<{
  stepState?: string;
  customColor?: string;
  size?: 'small' | 'medium' | 'large';
}>(({ theme, stepState, customColor, size = 'medium' }) => {
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { width: 24, height: 24, fontSize: '12px' };
      case 'large':
        return { width: 40, height: 40, fontSize: '16px' };
      default:
        return { width: 32, height: 32, fontSize: '14px' };
    }
  };

  const getStateColor = () => {
    switch (stepState) {
      case 'error':
        return (theme.palette.error as any)?.main;
      case 'warning':
        return (theme.palette.warning as any)?.main;
      case 'success':
      case 'completed':
        return (theme.palette.primary as any)?.main;
      case 'pending':
      case 'disabled':
        return theme.palette.grey?.[400];
      default:
        return customColor || (theme.palette.primary as any)?.main;
    }
  };

  return {
    ...getSizeStyles(),
    color: getStateColor(),
    '&.MuiStepIcon-active': {
      color: getStateColor(),
    },
    '&.MuiStepIcon-completed': {
      color: (theme.palette.primary as any)?.main,
    },
  };
});

/**
 * Enhanced Stepper component built on MUI Stepper
 *
 * Features:
 * - Built on MUI Stepper for consistency and accessibility
 * - Multiple step states with custom colors
 * - Custom connector styles (line, dashed, dotted, none)
 * - Clickable steps with navigation
 * - Custom icons and labels (labels appear below icons for horizontal steppers by default)
 * - Responsive sizing
 * - Full MUI theme integration
 */
export const Stepper = ({
  steps,
  activeStep = 0,
  orientation = 'horizontal',
  alternativeLabel,
  showDescriptions = false,
  sx,
  className,
  onStepClick,
  clickable = false,
  size = 'm',
  color = 'primary',
  showNumbers = true,
  renderStepIcon,
  renderStepLabel,
  connectorConfig,
  connector,
  iconConfig = DEFAULT_ICONS,
  styleConfig,
  iconContainerSx,
  labelSx,
  descriptionSx,
  getStepState,
  animation = {
    enableHover: true,
    enableTransitions: true,
    duration: 200,
  },
  accessibility = {},
  // Wizard mode props
  mode = 'indicator',
  showStepper = true,
  navigation,
  controlled = false,
  loading = false,
  onComplete,
  onCancel,
  onValidationError,
  onStepChange,
  persistProgress = false,
  storageKey,
  renderStepContent,
  renderNavigation,
  // Timeline mode props
  timelineExpanded = false,
  timelineHeading = 'Your Loan Journey',
  showDates = true,
  showSubsteps = true,
  stepWidths,
  ...muiStepperProps
}: StepperProps) => {
  const theme = useTheme();
  const [internalActiveStep, setInternalActiveStep] =
    React.useState(activeStep);
  const [completedSteps, setCompletedSteps] = React.useState<number[]>([]);
  const [validationErrors, setValidationErrors] = React.useState<
    Record<number, string>
  >({});

  // Use controlled or internal active step
  const currentActiveStep = controlled ? activeStep : internalActiveStep;

  // Determine if we're in wizard mode or timeline mode
  const isWizardMode =
    mode === 'wizard' ||
    (mode === 'auto' && steps.some((step) => step.content));

  const isTimelineMode = mode === 'timeline';

  // Validation function
  const canProceedToStep = React.useCallback(
    (stepIndex: number): boolean => {
      const step = steps[stepIndex];
      if (!step) return false;

      // Check if step has canProceed validation function
      if (typeof step.canProceed === 'function') {
        const result = step.canProceed();
        if (!result) {
          const errorMessage =
            step.validationMessage ||
            'Please complete this step before proceeding';
          setValidationErrors((prev) => ({
            ...prev,
            [stepIndex]: errorMessage,
          }));
          onValidationError?.(stepIndex, errorMessage);
          return false;
        } else {
          // Clear validation error for this step
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[stepIndex];
            return newErrors;
          });
        }
      }

      return true;
    },
    [steps, onValidationError]
  );

  // Navigation handlers
  const handleNext = React.useCallback(() => {
    const nextStep = currentActiveStep + 1;

    if (canProceedToStep(currentActiveStep)) {
      // Mark current step as completed
      setCompletedSteps((prev) => {
        if (!prev.includes(currentActiveStep)) {
          return [...prev, currentActiveStep];
        }
        return prev;
      });

      if (nextStep < steps.length) {
        if (!controlled) {
          setInternalActiveStep(nextStep);
        }
        onStepChange?.(nextStep, 'next');
      } else {
        // Wizard completed
        onComplete?.();
      }
    }
  }, [
    currentActiveStep,
    steps.length,
    canProceedToStep,
    controlled,
    onStepChange,
    onComplete,
  ]);

  const handlePrevious = React.useCallback(() => {
    const prevStep = currentActiveStep - 1;

    if (prevStep >= 0) {
      if (!controlled) {
        setInternalActiveStep(prevStep);
      }
      onStepChange?.(prevStep, 'previous');
    }
  }, [currentActiveStep, controlled, onStepChange]);

  const handleSkip = React.useCallback(() => {
    const nextStep = currentActiveStep + 1;

    if (nextStep < steps.length) {
      if (!controlled) {
        setInternalActiveStep(nextStep);
      }
      onStepChange?.(nextStep, 'next');
    }
  }, [currentActiveStep, steps.length, controlled, onStepChange]);

  const handleFinish = React.useCallback(async () => {
    if (canProceedToStep(currentActiveStep)) {
      // Mark current step as completed
      setCompletedSteps((prev) => {
        if (!prev.includes(currentActiveStep)) {
          return [...prev, currentActiveStep];
        }
        return prev;
      });

      await onComplete?.();
    }
  }, [currentActiveStep, canProceedToStep, onComplete]);

  // Navigation state
  const canGoNext = currentActiveStep < steps.length - 1 && !loading;
  const canGoPrevious = currentActiveStep > 0 && !loading;
  const isLastStep = currentActiveStep === steps.length - 1;
  const currentStep = steps[currentActiveStep];

  // Update internal state when activeStep prop changes
  React.useEffect(() => {
    if (controlled) {
      setInternalActiveStep(activeStep);
    }
  }, [activeStep, controlled]);

  // Get size configuration from theme
  const getSizeConfig = () => {
    const themeSize = (theme as any).size?.stepper;
    const sizeConfig = themeSize ? themeSize[size] : null;
    if (sizeConfig) {
      return {
        width: parseInt(sizeConfig.width as string) || 32,
        height: parseInt(sizeConfig.height as string) || 32,
        fontSize: sizeConfig.fontSize || '14px',
      };
    }
    // Fallback for standard sizes
    switch (size) {
      case 's':
        return { width: 24, height: 24, fontSize: '12px' };
      case 'l':
        return { width: 40, height: 40, fontSize: '16px' };
      case 'xl':
        return { width: 48, height: 48, fontSize: '18px' };
      default:
        return { width: 32, height: 32, fontSize: '14px' };
    }
  };

  // Enhanced step state determination with custom logic
  const determineStepState = (step: Step, index: number): StepState => {
    // Use custom state determination if provided
    if (getStepState) {
      return getStepState(step, index, activeStep);
    }

    // Default logic
    if (step.disabled) return 'disabled';
    if (step.state) return step.state as StepState;
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'active';
    return 'pending';
  };

  // Merge default icon config with provided config
  const mergedIconConfig = {
    ...DEFAULT_ICONS,
    ...iconConfig,
    showNumbers:
      iconConfig?.showNumbers !== undefined
        ? iconConfig.showNumbers
        : showNumbers,
  };

  // Handle step click
  const handleStepClick = (index: number) => {
    if (!clickable || !onStepClick) return;
    const step = steps[index];
    if (step && !step.disabled) {
      onStepClick(index, step);
    }
  };

  // Enhanced custom step icon logic with new configuration
  const getCustomStepIcon = (step: Step, index: number, state: StepState) => {
    if (renderStepIcon) {
      return renderStepIcon(
        step,
        index,
        state === 'active',
        state === 'completed',
        state
      );
    }

    if (step.icon) {
      return step.icon;
    }

    // Return null to use our enhanced NumberedStepIcon component
    return null;
  };

  // Enhanced connector component with style configuration
  const getConnectorComponent = (): React.ReactElement | null => {
    // Check for custom connector component first
    if (
      connectorConfig?.component &&
      React.isValidElement(connectorConfig.component)
    ) {
      return connectorConfig.component;
    }

    // Handle backward compatibility with connector prop
    const connectorType = connectorConfig?.type || connector || 'line';

    if (connectorType === 'none') {
      return <Box sx={{ display: 'none' }} />;
    }

    // Get icon size for proper connector positioning
    const sizeConfig = getSizeConfig();
    const iconSize = sizeConfig.height;

    return (
      <CustomStepConnector
        connectorType={connectorType}
        connectorStyle={connectorConfig?.style}
        iconSize={iconSize}
      />
    );
  };

  // Timeline mode rendering
  if (isTimelineMode) {
    // Default step widths in pixels
    const defaultStepWidthsInPixels = [180, 220, 170, 170, 190, 170];
    // Convert provided stepWidths (in pixels) or use defaults
    const widthsInPixels = stepWidths || defaultStepWidthsInPixels;
    const themeColors = getThemeColors(theme);

    return (
      <Box>
        {/* Timeline Heading - only show when expanded */}
        {timelineExpanded && timelineHeading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              padding: theme.unit(0),
              gap: theme.unit(2), // 8px (2 × 4 = 8)
              width: '100%',
              height: theme.unit(8), // 32px (8 × 4 = 32)
              mt: theme.unit(2), // 8px (2 × 4 = 8)
            }}
          >
            <Typography
              sx={{
                ...theme.typography.M3.title.medium,
                fontWeight: 600,
                color: themeColors.black,
              }}
            >
              {timelineHeading}
            </Typography>
          </Box>
        )}

        {/* Timeline Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            padding: `${theme.unit(2.5)} ${theme.unit(0)}`, // 10px 0px (2.5 × 4 = 10)
            gap: theme.unit(0),
            width: '100%',
            height: timelineExpanded ? theme.unit(32.5) : theme.unit(18.5), // 130px : 74px (32.5 × 4 = 130, 18.5 × 4 = 74)
            background: themeColors.surface,
            borderRadius: theme.unit(0.75), // 3px (0.75 × 4 = 3)
            ...sx,
          }}
        >
          {steps
            .map((step, index) => {
              const stepElements = [];
              // Use step.status for timeline mode
              const isCompleted = isStepStatus(
                step.status,
                StepStatus.COMPLETED
              );
              const isInProgress = isStepStatus(
                step.status,
                StepStatus.IN_PROGRESS
              );
              const isPending = isStepStatus(step.status, StepStatus.PENDING);

              // Step Box
              stepElements.push(
                <Box
                  key={`step-${step.id}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: theme.unit(0),
                    width: `${widthsInPixels[index] || 170}px`, // Use pixels directly
                    height: timelineExpanded
                      ? index === 0
                        ? theme.unit(22.5) // 90px (22.5 × 4 = 90)
                        : index === 1
                          ? theme.unit(27.5) // 110px (27.5 × 4 = 110)
                          : theme.unit(22.5) // 90px (22.5 × 4 = 90)
                      : theme.unit(13.5), // 54px (13.5 × 4 = 54)
                    flex: 'none',
                  }}
                >
                  {/* Step Item */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: `${theme.unit(2.5)} ${theme.unit(2.5)} ${theme.unit(2.5)} ${theme.unit(0)}`, // 10px 10px 10px 0px (2.5 × 4 = 10)
                      gap: theme.unit(2.5), // 10px (2.5 × 4 = 10)
                      height: theme.unit(13.5), // 54px (13.5 × 4 = 54)
                    }}
                  >
                    {/* Status Indicator */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: theme.unit(0),
                        width: theme.unit(7), // 28px (7 × 4 = 28)
                        height: theme.unit(7), // 28px (7 × 4 = 28)
                        background: getTimelineStatusBackground(
                          step.status,
                          themeColors
                        ),
                        border: getTimelineStatusBorder(
                          step.status,
                          themeColors,
                          theme
                        ),
                        borderRadius: '50%',
                        position: 'relative',
                      }}
                    >
                      {isCompleted && (
                        <Box
                          sx={{
                            width: theme.unit(3.75), // 15px (3.75 × 4 = 15)
                            height: theme.unit(3), // 12px (3 × 4 = 12)
                            background: themeColors.surface,
                            WebkitMask:
                              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E\") center/contain no-repeat",
                            mask: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E\") center/contain no-repeat",
                          }}
                        />
                      )}
                      {isInProgress && (
                        <CircularProgress
                          size={theme.unit(7)} // 28px (7 × 4 = 28)
                          thickness={6}
                          sx={{ color: themeColors.primary }}
                        />
                      )}
                    </Box>

                    {/* Text Content */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        padding: theme.unit(0),
                        gap: theme.unit(1), // 4px (1 × 4 = 4)
                        height: theme.unit(8.5), // 34px (8.5 × 4 = 34)
                      }}
                    >
                      {/* Label */}
                      <Typography
                        sx={{
                          ...theme.typography.M3.body.medium,
                          color: themeColors.black,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {/* Date */}
                      {showDates && step.date && (
                        <Typography
                          sx={{
                            ...theme.typography.M3.body.small,
                            color: themeColors.onSurfaceVariant,
                          }}
                        >
                          {step.date}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Substeps Container - only show when expanded */}
                  {timelineExpanded &&
                    showSubsteps &&
                    step.substeps &&
                    step.substeps.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          padding: `${theme.unit(0)} ${theme.unit(0)} ${theme.unit(0)} ${theme.unit(9.5)}`, // 0px 0px 0px 38px (9.5 × 4 = 38)
                          gap: theme.unit(1), // 4px (1 × 4 = 4)
                        }}
                      >
                        {step.substeps.map((substep, subIndex) => (
                          <Box
                            key={`substep-${substep.label}`}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              padding: theme.unit(0),
                              gap: theme.unit(2.5), // 10px (2.5 × 4 = 10)
                              height: theme.unit(4), // 16px (4 × 4 = 16)
                            }}
                          >
                            {/* Substep Dot */}
                            <Box
                              sx={{
                                width: theme.unit(2), // 8px (2 × 4 = 8)
                                height: theme.unit(2), // 8px (2 × 4 = 8)
                                background: getSubstepDotColor(
                                  substep.status,
                                  themeColors
                                ),
                                borderRadius: '50%',
                              }}
                            />
                            {/* Substep Label */}
                            <Typography
                              sx={{
                                ...theme.typography.M3.body.small,
                                color: themeColors.black,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {substep.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Box>
              );

              // Connector Line (except after last step)
              if (index < steps.length - 1) {
                stepElements.push(
                  <Box
                    key={`connector-${step.id}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: `${theme.unit(0)} ${theme.unit(2)}`, // 0px 8px (2 × 4 = 8)
                      gap: theme.unit(2.5), // 10px (2.5 × 4 = 10)
                      width: theme.unit(10), // 40px (10 × 4 = 40)
                      height: theme.unit(13.5), // 54px (13.5 × 4 = 54)
                    }}
                  >
                    <Box
                      sx={{
                        width: theme.unit(6), // 24px (6 × 4 = 24)
                        height: theme.unit(0.5), // 2px (0.5 × 4 = 2)
                        background: themeColors.connectorDefault,
                        borderRadius: theme.unit(0),
                      }}
                    />
                  </Box>
                );
              }

              return stepElements;
            })
            .flat()}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Conditionally render stepper indicator */}
      {(!isWizardMode || showStepper) && (
        <Box
          sx={{
            // Elements - Inner container
            // Width: 8 steps × 140px = 1120px (all steps same width for consistent centering)
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            padding: 0,
            margin: '0 auto',
            width: 1120, // Fixed: 8 steps × 140px = 1120px
            maxWidth: 1120,
            height: 66,
            // Inside auto layout
            flex: 'none',
            order: 0,
            flexGrow: 0,
          }}
        >
          <MuiStepper
            activeStep={currentActiveStep}
            orientation={orientation}
            alternativeLabel={true} // Keep alternativeLabel for labels below badges
            connector={getConnectorComponent()}
            className={className}
            sx={{
              // Stepper wizard layout
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              padding: 0,
              width: '100%',
              height: 66,
              gap: 0,
              // Merge with custom sx prop
              ...sx,
            }}
            // Enhanced accessibility
            {...(accessibility?.ariaLabel && {
              'aria-label': accessibility.ariaLabel,
            })}
            {...(accessibility?.ariaDescription && {
              'aria-describedby': accessibility.ariaDescription,
            })}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={steps.length - 1}
            aria-valuenow={currentActiveStep}
            aria-valuetext={`Step ${currentActiveStep + 1} of ${steps.length}: ${steps[currentActiveStep]?.label || ''}`}
            {...muiStepperProps}
          >
            {steps.map((step, index) => {
              const state = determineStepState(step, index);
              const isCompleted = state === 'completed';
              const isActive = state === 'active';
              const customIcon = getCustomStepIcon(step, index, state);
              const isLastStep = index === steps.length - 1;

              return (
                <MuiStep
                  key={step.id}
                  completed={isCompleted}
                  disabled={step.disabled || state === 'disabled'}
                  onClick={clickable ? () => handleStepClick(index) : undefined}
                  sx={{
                    // Stepper var - step container
                    // Width calculation: badge (24px) + gap (6px) + connector (104px) + gap (6px) = 140px
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    padding: 0,
                    gap: 0,
                    position: 'relative',
                    width: 140, // 140px = badge + gaps + connector
                    minWidth: 140,
                    maxWidth: 140,
                    minHeight: 66,
                    height: 'auto',
                    overflow: 'visible',
                    // Inside auto layout
                    flex: 'none',
                    flexGrow: 0,
                    flexShrink: 0,
                    order: index,
                    cursor:
                      clickable && !step.disabled && state !== 'disabled'
                        ? 'pointer'
                        : 'default',
                    '& .MuiStepLabel-root': {
                      cursor:
                        clickable && !step.disabled && state !== 'disabled'
                          ? 'pointer'
                          : 'default',
                      padding: 0,
                    },
                    // Enhanced state-based styling
                    ...(state === StepStatus.PENDING && {
                      '& .MuiStepIcon-root': {
                        backgroundColor:
                          styleConfig?.pending?.backgroundColor ||
                          theme.palette.grey?.[200],
                        color:
                          styleConfig?.pending?.color ||
                          theme.palette.grey?.[500],
                      },
                      '& .MuiStepLabel-label': {
                        color:
                          styleConfig?.pending?.color ||
                          theme.palette.grey?.[500],
                        opacity: 0.7,
                      },
                    }),
                    ...(state === StepStatus.DISABLED && {
                      opacity: 0.5,
                      pointerEvents: 'none',
                    }),
                    // Apply custom step styles
                    ...step.sx,
                    // Apply animation styles
                    ...(animation?.enableTransitions && {
                      transition: `all ${animation.duration || 200}ms ease-in-out`,
                    }),
                    ...(animation?.enableHover &&
                      clickable && {
                        '&:hover': {
                          '& .MuiStepIcon-root': {
                            transform: 'scale(1.05)',
                          },
                        },
                      }),
                  }}
                  // Add accessibility attributes
                  {...(accessibility?.stateLabels?.[state] && {
                    'aria-label': accessibility.stateLabels[state],
                  })}
                >
                  <StepLabel
                    optional={
                      step.optional ? (
                        <Typography variant="caption">Optional</Typography>
                      ) : undefined
                    }
                    StepIconComponent={
                      customIcon
                        ? () => customIcon
                        : (props) => (
                            <NumberedStepIcon
                              {...props}
                              active={isActive}
                              completed={isCompleted}
                              state={state}
                              size={size}
                              theme={theme}
                              iconConfig={mergedIconConfig}
                              styleConfig={styleConfig}
                              enableTransitions={animation?.enableTransitions}
                              enableHover={animation?.enableHover}
                              duration={animation?.duration}
                            />
                          )
                    }
                    sx={{
                      // StepLabel - badge above, label below
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: 0,
                      gap: '10px',
                      width: '100%', // Take full width of step container
                      minWidth: 24,
                      minHeight: 66,
                      height: 'auto',
                      // Inside auto layout
                      flex: 'none',
                      order: 0,
                      flexGrow: 0,
                      '& .MuiStepLabel-iconContainer': {
                        padding: 0,
                        marginBottom: 0,
                        height: 24, // Badge height
                      },
                      '& .MuiStepLabel-labelContainer': {
                        // .MasterStepperTitle - Auto layout
                        display: 'flex !important' as any,
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: 0,
                        minHeight: 20,
                        height: 20, // Fixed height to prevent shifting
                        width: 'auto', // Allow label to take natural width
                        minWidth: 24, // At least badge width
                        // Inside auto layout
                        flex: 'none',
                        order: 1,
                        flexGrow: 0,
                        marginTop: '10px !important' as any, // 10px gap between badge and title - enforce with !important
                        overflow: 'visible', // Ensure label is not clipped
                        position: 'relative', // For consistent positioning
                      },
                      '& .MuiStepLabel-label': {
                        // Text Step - M3/label/large
                        display: 'block !important' as any, // Force display
                        visibility: 'visible !important' as any, // Force visibility
                        padding: 0,
                        margin: 0,
                        minHeight: 20,
                        height: 20, // Fixed height to prevent vertical shifting
                        marginTop: '0 !important' as any,
                        whiteSpace: 'nowrap', // Prevent text wrapping
                        // Typography - M3/label/large
                        ...theme.typography.M3.label.large,
                        fontWeight: 700,
                        textAlign: 'center',
                        verticalAlign: 'top', // Ensure baseline alignment
                        // Color based on state
                        color:
                          isActive || isCompleted
                            ? (`${getThemeColors(theme).black} !important` as any)
                            : (`${getThemeColors(theme).onSurfaceVariant} !important` as any),
                        opacity: '1 !important' as any,
                      },
                    }}
                  >
                    {renderStepLabel
                      ? renderStepLabel(
                          step,
                          index,
                          isActive,
                          isCompleted,
                          state
                        )
                      : step.label}
                  </StepLabel>
                  {orientation === 'vertical' &&
                    showDescriptions &&
                    step.description && (
                      <StepContent>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            ...descriptionSx,
                          }}
                        >
                          {step.description}
                        </Typography>
                      </StepContent>
                    )}
                </MuiStep>
              );
            })}
          </MuiStepper>
        </Box>
      )}

      {/* Wizard Mode Content and Navigation */}
      {isWizardMode && (
        <Box sx={{ mt: 3 }}>
          {/* Step Content */}
          <Box sx={{ minHeight: 200, mb: 3 }}>
            {renderStepContent ? (
              renderStepContent(currentStep, currentActiveStep)
            ) : currentStep?.content ? (
              <Box>{currentStep.content}</Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">
                  No content defined for step: {currentStep?.label}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Validation Error */}
          {validationErrors[currentActiveStep] && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                }}
              >
                ⚠️ {validationErrors[currentActiveStep]}
              </Typography>
            </Box>
          )}

          {/* Navigation */}
          {navigation?.show !== false && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: navigation?.alignment || 'space-between',
                alignItems: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                ...(navigation?.position === 'top' && {
                  order: -1,
                  mb: 3,
                  mt: 0,
                  borderTop: 'none',
                  borderBottom: 1,
                }),
              }}
            >
              {renderNavigation ? (
                renderNavigation(
                  currentActiveStep,
                  steps.length,
                  canGoNext,
                  canGoPrevious,
                  {
                    onNext: handleNext,
                    onPrevious: handlePrevious,
                    onFinish: handleFinish,
                    onSkip: handleSkip,
                  }
                )
              ) : (
                <>
                  {/* Previous Button */}
                  <Box>
                    {navigation?.showPrevious !== false && canGoPrevious && (
                      <Button
                        variant="outlined"
                        onClick={handlePrevious}
                        disabled={loading}
                        startIcon={navigation?.previousIcon}
                        sx={navigation?.buttonSx}
                      >
                        {navigation?.previousText || 'Previous'}
                      </Button>
                    )}
                  </Box>

                  {/* Center content (step indicator, etc.) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {navigation?.showStepInfo !== false && (
                      <Typography variant="body2" color="text.secondary">
                        Step {currentActiveStep + 1} of {steps.length}
                      </Typography>
                    )}

                    {currentStep?.skippable && (
                      <Button
                        variant="text"
                        onClick={handleSkip}
                        disabled={loading}
                        size="s"
                      >
                        {navigation?.skipText || 'Skip'}
                      </Button>
                    )}
                  </Box>

                  {/* Next/Finish Button */}
                  <Box>
                    {isLastStep
                      ? navigation?.showFinish !== false && (
                          <Button
                            variant="filled"
                            onClick={handleFinish}
                            disabled={loading}
                            endIcon={
                              loading ? (
                                <CircularProgress size={16} />
                              ) : (
                                navigation?.finishIcon
                              )
                            }
                            sx={navigation?.buttonSx}
                          >
                            {navigation?.finishText || 'Finish'}
                          </Button>
                        )
                      : navigation?.showNext !== false && (
                          <Button
                            variant="filled"
                            onClick={handleNext}
                            disabled={loading}
                            endIcon={navigation?.nextIcon}
                            sx={navigation?.buttonSx}
                          >
                            {navigation?.nextText || 'Next'}
                          </Button>
                        )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Stepper;
